/**
 * KND-4b — what the e-mail part of the "Kündigungsbestätigung" shows, read off
 * the server's answers. Pure functions, no requests.
 *
 * NOTHING IS DECIDED HERE. Whether a version may be sent is the server's
 * verdict: the confirmations GET says which version is current, whether it is
 * stale and what its delivery attempts are (`versions[].deliveries`, newest
 * attempt first); the preview says whether a send is possible right now and
 * why not. This module maps those answers to German text and to the one
 * action the panel may offer. The server re-checks every rule on the send.
 *
 * CONTROLLED COPY. A refusal travels as a stable `code` (and, for a refused
 * provider call, an `error_code`). Each known code has its own neutral
 * sentence here; an unknown code gets a generic one. The server's own
 * sentence, a provider text or a technical id never reaches the screen
 * through this module.
 */
import { formatDateTime } from "../../../../utils/datetime";

const DELIVERY_STATUSES = new Set(["sending", "sent", "failed"]);

/** Every delivery attempt of a version, as the server ordered them (newest
 *  attempt first). Anything that is not an attempt is dropped. */
export function deliveriesOf(version) {
  const rows = Array.isArray(version?.deliveries) ? version.deliveries : [];
  return rows.filter((d) => d && typeof d === "object" && d.id);
}

/** The newest attempt — the one a resend has to name. */
export function latestDelivery(version) {
  return deliveriesOf(version)[0] || null;
}

/** The newest attempt the provider accepted, if any. */
export function lastSentDelivery(version) {
  return deliveriesOf(version).find((d) => d.status === "sent") || null;
}

/**
 * `none` — never sent; `sent` / `failed` / `sending` — the latest attempt's
 * status; `unknown` — a status this client cannot read (never offered an
 * action, never read as "not sent").
 */
export function deliveryState(version) {
  const latest = latestDelivery(version);
  if (!latest) return "none";
  return DELIVERY_STATUSES.has(latest.status) ? latest.status : "unknown";
}

/** The current version's row of the history — the one that carries its
 *  deliveries. `null` when the answer does not contain it (no action then). */
export function currentVersionOf(data) {
  const currentId = data?.current_confirmation?.id ?? null;
  if (!currentId || !Array.isArray(data?.versions)) return null;
  return (
    data.versions.find((v) => v && v.id === currentId && v.lifecycle === "current") ||
    null
  );
}

/**
 * The one action the panel may offer for the current version, or null.
 *
 * Only the current, not-stale version of a tenancy the block may act on.
 * `sending` offers nothing (the outcome is open — a second mail is exactly
 * what the open attempt exists to prevent); an unreadable status offers
 * nothing either.
 */
export function deliveryAction({ view, state, canAct }) {
  if (!canAct || view !== "current") return null;
  if (state === "none") return "send";
  if (state === "sent" || state === "failed") return "resend";
  return null;
}

/** Which dialog a click opens: a first send, a resend of a sent version, or a
 *  new attempt after a failed one. */
export function dialogModeFor(state) {
  if (state === "sent") return "resend";
  if (state === "failed") return "retry";
  return "first";
}

// ── Text ────────────────────────────────────────────────────────────────

export const SENDING_EXPLANATION =
  "Das Ergebnis des letzten Versands ist nicht bestätigt. Die E-Mail wurde " +
  "möglicherweise versendet. Diese Version wird deshalb nicht erneut gesendet.";

export const STALE_NOTICE = "Bitte zuerst eine neue Version erstellen.";

/** The panel's headline for the current version's delivery state. */
export function deliveryHeadline(version) {
  const state = deliveryState(version);
  if (state === "none") return "Noch nicht versendet";
  if (state === "sent") {
    return `Gesendet am ${formatDateTime(latestDelivery(version).sent_at)}`;
  }
  if (state === "failed") return "Versand fehlgeschlagen";
  if (state === "sending") return "Versandstatus wird noch geklärt";
  return "Versandstatus unbekannt";
}

/** "an mieter@example.ch" — the address the attempt went to, as the server
 *  recorded it at the time (never today's tenant address). */
export function recipientText(delivery) {
  const address = typeof delivery?.recipient_email === "string"
    ? delivery.recipient_email.trim()
    : "";
  return address ? `an ${address}` : null;
}

const FAILURE_REASONS = {
  provider_rejected: "Der E-Mail-Dienst hat die Nachricht abgelehnt.",
  provider_unavailable: "Der E-Mail-Dienst war nicht erreichbar.",
  transport_not_configured: "Der E-Mail-Versand ist nicht eingerichtet.",
};

/** Why a failed attempt failed, from its controlled `error_code`. */
export function failureReasonText(errorCode) {
  return FAILURE_REASONS[errorCode] || "Die E-Mail konnte nicht versendet werden.";
}

/** "Zuletzt gesendet am … an …" — shown when the latest attempt failed but an
 *  earlier one was accepted, so the failure does not hide that the tenant has
 *  the letter already. */
export function earlierSentText(version) {
  if (deliveryState(version) !== "failed") return null;
  const sent = lastSentDelivery(version);
  if (!sent) return null;
  const to = recipientText(sent);
  return [`Zuletzt gesendet am ${formatDateTime(sent.sent_at)}`, to]
    .filter(Boolean)
    .join(" ");
}

/**
 * The short delivery label of a version in a list (the history lines of the
 * panel, the documents workspace). `withNone` decides whether a version that
 * was never sent says so or stays silent.
 */
export function deliveryListLabel(version, { withNone = true } = {}) {
  const state = deliveryState(version);
  if (state === "none") return withNone ? "Noch nicht versendet" : null;
  if (state === "sent") {
    return `Gesendet am ${formatDateTime(latestDelivery(version).sent_at)}`;
  }
  if (state === "failed") return "Versand fehlgeschlagen";
  if (state === "sending") return "Versandstatus offen";
  return "Versandstatus unbekannt";
}

// ── Controlled codes → neutral copy ─────────────────────────────────────

const CODE_COPY = {
  confirmation_superseded: "Diese Version wurde durch eine neuere Version ersetzt.",
  previous_termination: "Diese Version gehört zu einem früheren Kündigungsvorgang.",
  termination_withdrawn: "Die Kündigung wurde zurückgenommen.",
  tenancy_cancelled: "Das Mietverhältnis ist storniert.",
  not_tenant_termination:
    "Die Kündigung ist nicht als Kündigung durch die Mietpartei erfasst.",
  confirmation_stale:
    "Die Daten der Kündigung haben sich geändert. Bitte zuerst eine neue Version erstellen.",
  delivery_in_progress: "Der Versandstatus dieser Version ist noch nicht geklärt.",
  recipient_missing: "Für die Mietpartei ist keine E-Mail-Adresse hinterlegt.",
  recipient_invalid:
    "Die hinterlegte E-Mail-Adresse der Mietpartei kann nicht verwendet werden.",
  sender_missing: "Für diese Organisation ist kein E-Mail-Absender eingerichtet.",
  confirmation_unreadable: "Die gespeicherten Angaben dieser Version sind unvollständig.",
  pdf_missing: "Das PDF dieser Version ist nicht verfügbar.",
  storage_unavailable: "Der Dokumentenspeicher ist nicht verfügbar.",
  storage_error: "Das PDF konnte nicht geladen werden.",
  pdf_mismatch: "Das gespeicherte PDF entspricht nicht der erstellten Version.",
  delivery_state_changed:
    "Der Versandstatus hat sich inzwischen geändert. Die Ansicht wurde aktualisiert.",
  delivery_unconfirmed:
    "Der Versandstatus konnte nicht bestätigt werden. Die E-Mail wurde möglicherweise " +
    "versendet. Bitte nicht erneut senden.",
};

const DELIVERY_FAILED = "Die Kündigungsbestätigung konnte nicht versendet werden.";
const GENERIC_ISSUE = "Diese Version kann derzeit nicht versendet werden.";
const GENERIC_SEND_ERROR = "Der Versand konnte nicht ausgeführt werden.";
const GENERIC_PREVIEW_ERROR = "Die Versandvorschau konnte nicht geladen werden.";

/** The neutral sentence of one preview issue. */
export function issueText(code) {
  return CODE_COPY[code] || GENERIC_ISSUE;
}

/** Every preview issue as text, in the server's order, each sentence once. */
export function previewIssueTexts(preview) {
  const issues = Array.isArray(preview?.issues) ? preview.issues : [];
  const texts = issues.map((issue) => issueText(issue?.code));
  return [...new Set(texts)];
}

function statusFallback(err, generic) {
  if (err?.status === 429) {
    return "Zu viele Versandversuche. Bitte später erneut versuchen.";
  }
  if (err?.status === 404) return "Diese Version wurde nicht gefunden.";
  if (err?.status === 403) return "Keine Berechtigung für den Versand.";
  return generic;
}

/** The toast after a refused or failed send. */
export function deliveryErrorText(err) {
  const code = err?.code;
  if (code === "delivery_failed") {
    return `${DELIVERY_FAILED} ${failureReasonText(err?.errorCode)}`;
  }
  if (code && CODE_COPY[code]) return CODE_COPY[code];
  return statusFallback(err, GENERIC_SEND_ERROR);
}

/** The toast when the preview itself could not be loaded. */
export function previewErrorText(err) {
  const code = err?.code;
  if (code && CODE_COPY[code]) return CODE_COPY[code];
  return statusFallback(err, GENERIC_PREVIEW_ERROR);
}

export const SENT_TEXT = "Kündigungsbestätigung wurde per E-Mail versendet.";
export const ALREADY_SENT_TEXT =
  "Die Kündigungsbestätigung war bereits versendet. Es wurde keine weitere E-Mail verschickt.";
export const PREVIEW_UNREADABLE = GENERIC_PREVIEW_ERROR;

const filled = (value) => typeof value === "string" && value.trim() !== "";

/**
 * Is this a preview this client can show for THIS version? A sendable answer
 * must name everything the dialog shows; a refusing one must say why. Anything
 * else is unreadable — and offers no send (fail closed).
 */
export function isReadablePreview(preview, confirmationId) {
  if (!preview || typeof preview !== "object") return false;
  if (preview.confirmation_id !== confirmationId) return false;
  if (typeof preview.sendable !== "boolean") return false;
  const issues = Array.isArray(preview.issues) ? preview.issues : [];
  if (!preview.sendable) return issues.length > 0;
  if (issues.length) return false;
  return ["recipient", "from_name", "from_email", "subject", "attachment_filename"].every(
    (key) => filled(preview[key])
  );
}
